import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { filter, Observable, of, take } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { FetchAllCurrencies } from 'src/app/features/customers/store/customers.actions';
import { ICurrencyPrice } from '../../models/products.models';
interface AddedCurrencyControl {
  data: ICurrency;
  controls: {
    retail_price: FormGroup;
    cost: FormGroup;
  };
}
@Component({
  selector: 'app-edit-product-pricing',
  templateUrl: './edit-product-pricing.component.html',
  styleUrls: ['./edit-product-pricing.component.scss']
})
export class EditProductPricingComponent implements OnInit {
  @ViewChild('dropdown') dropdownRef!: ElementRef;
  @ViewChild('button') buttonRef!: ElementRef;
  form!: FormGroup
  currencies$: Observable<ICurrency[]> = of([]);
  default_currency!: ICurrency | null | undefined;
  currencies: ICurrency[] = [];
  availableCurrencies: ICurrency[] = [];
  addedCurrencies: AddedCurrencyControl[] = [];
  showCurrencyDropdown = false;
  currenciesDropdown = {
    top: 0,
    left: 0,
    minWidth: 0
  };

  constructor(
    public dialogRef: MatDialogRef<EditProductPricingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) { }

  ngOnInit(): void {
    this.store.dispatch(FetchAllCurrencies());
    this.store.select((state) => state.customers.allCurrencies).pipe(
      filter((currencies) => Array.isArray(currencies) && currencies.length > 0),
      take(1)
    ).subscribe((currencies) => {
      this.handleCurrencies(currencies);
    });
    this.form = this.fb.group({
      sales_price: [this.data?.sales_price ?? '', [Validators.required, Validators.min(0)]],
      cost: [this.data?.cost ?? '', [Validators.required, Validators.min(0)]],
    })
  }

  private handleCurrencies(currencies: ICurrency[]): void {
    this.addedCurrencies = [];
    this.currencies = currencies.filter(currency => !currency.is_default);

    this.availableCurrencies = this.currencies.filter(currency =>
      !this.data.currencyPrices?.some((price: ICurrencyPrice) => price.currency_id === currency.id)
    );

    this.currencies.forEach((currency: ICurrency) => {
      const existedRetailPrice = this.data.currencyPrices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === currency.id && currencyPrice.type === 'retail_price'
      );
      const existedCost = this.data.currencyPrices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === currency.id && currencyPrice.type === 'cost'
      );

      if (existedRetailPrice || existedCost) {
        this.addCurrency(currency, existedRetailPrice?.amount || null, existedCost?.amount || null);
      }
    });

    this.default_currency = currencies.find(currency => currency.is_default);
  }


  sanitize(event: any) {
    if (event.target.value < 0) {
      event.target.value = 0;
    }
  }

  save(): boolean | void {
    if(this.form.invalid){
      this.form.markAllAsTouched()
      return false
    }
    this.dialogRef.close({
      ...this.form.value,
      currency_prices: this.addedCurrencies.flatMap(c => [
        c.controls.retail_price.value,
        c.controls.cost.value
      ]),
      addedCurrencies: this.addedCurrencies
    })
  }

  closed(){
    this.dialogRef.close()
  }

  removeCurrency(currencyId: number): void {
    const index = this.addedCurrencies?.findIndex(c => c.data.id === currencyId);
    if (index > -1) {
      this.availableCurrencies.push(this.addedCurrencies[index].data);
      this.addedCurrencies.splice(index, 1);
    }
  }

  addCurrency(currency: ICurrency, retail_amount: number | null = null, cost_amount: number | null = null): void {
    const createControls = (currency: ICurrency, retail_amount: number | null = null, cost_amount: number | null = null) => ({
      retail_price: this.fb.group({
        currency_id: [currency.id, Validators.required],
        type: ['retail_price'],
        amount: [retail_amount, Validators.min(0)],
        name: [currency.name],
        symbol: [currency.symbol]
      }),
      cost: this.fb.group({
        currency_id: [currency.id, Validators.required],
        type: ['cost'],
        amount: [cost_amount, Validators.min(0)],
        name: [currency.name],
        symbol: [currency.symbol]
      })
    });

    this.addedCurrencies.push({
      data: currency,
      controls: createControls(currency, retail_amount, cost_amount)
    });
    this.availableCurrencies = this.availableCurrencies.filter(c => c.id !== currency.id);
    this.showCurrencyDropdown = false;
  }

  openCurrenciesDropdown(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();

    this.currenciesDropdown = {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      minWidth: rect.width
    };

    this.showCurrencyDropdown = true;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const clickedInsideDropdown = this.dropdownRef?.nativeElement.contains(event.target);
    const clickedButton = this.buttonRef?.nativeElement.contains(event.target);

    if (!clickedInsideDropdown && !clickedButton) {
      this.showCurrencyDropdown = false;
    }
  }
}
